import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IEvaluation, defaultValue } from 'app/shared/model/evaluation.model';

export type EvaluationNotesStatus = 'Non saisie' | 'En cours' | 'Terminée';

interface EvaluationEntityState extends EntityState<IEvaluation> {
  noteStatusMap: Record<string, EvaluationNotesStatus>;
  notesStatusRequestId: string | null;
}

const initialState: EvaluationEntityState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IEvaluation>,
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
  noteStatusMap: {},
  notesStatusRequestId: null,
};

const apiUrl = 'api/evaluations';

export const getAllEvaluations = createAsyncThunk(
  'evaluation/fetch_all',
  async ({ ecoleId, anneeScolaireId }: { ecoleId: string; anneeScolaireId: string }) => {
    const requestUrl = `${apiUrl}/all?ecoleId=${ecoleId}&anneeScolaireId=${anneeScolaireId}`;
    return axios.get<IEvaluation[]>(requestUrl);
  }
);

export const getEvaluations = createAsyncThunk(
  'evaluation/fetch_list',
  async ({ classeId, periodeId }: { classeId: string; periodeId: string }) => {
    const requestUrl = `${apiUrl}?classeId=${classeId}&periodeId=${periodeId}`;
    return axios.get<IEvaluation[]>(requestUrl);
  }
);

export const getEvaluation = createAsyncThunk(
  'evaluation/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IEvaluation>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEvaluationNotesStatuses = createAsyncThunk(
  'evaluation/fetch_notes_statuses',
  async (evaluationIds: string[]) => {
    const response = await axios.post<Array<{ evaluationId: string; statut?: EvaluationNotesStatus }>>(
      '/api/notes/evaluations/status',
      evaluationIds
    );
    const noteStatusMap: Record<string, EvaluationNotesStatus> = {};
    (response.data || []).forEach(item => {
      if (item?.evaluationId) {
        noteStatusMap[item.evaluationId] = item.statut || 'Non saisie';
      }
    });
    return noteStatusMap;
  },
  { serializeError: serializeAxiosError }
);

export const createEvaluation = createAsyncThunk(
  'evaluation/create_entity',
  async (entity: IEvaluation, thunkAPI) => {
    const result = await axios.post<IEvaluation>(apiUrl, cleanEntity(entity));
    if (entity.ecoleId && entity.anneeScolaireId) {
      thunkAPI.dispatch(getAllEvaluations({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId }));
    }
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEvaluation = createAsyncThunk(
  'evaluation/update_entity',
  async (entity: IEvaluation, thunkAPI) => {
    const result = await axios.put<IEvaluation>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    if (entity.ecoleId && entity.anneeScolaireId) {
      thunkAPI.dispatch(getAllEvaluations({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId }));
    }
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEvaluation = createAsyncThunk(
  'evaluation/delete_entity',
  async (entity: { id: string; ecoleId: string; anneeScolaireId: string }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<IEvaluation>(requestUrl);
    thunkAPI.dispatch(getAllEvaluations({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export type EvaluationState = Readonly<typeof initialState>;

export const EvaluationSlice = createSlice({
  name: 'evaluation',
  initialState: initialState as EvaluationState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEvaluation.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getEvaluationNotesStatuses.pending, (state, action) => {
        state.notesStatusRequestId = action.meta.requestId;
      })
      .addCase(getEvaluationNotesStatuses.fulfilled, (state, action) => {
        if (state.notesStatusRequestId !== action.meta.requestId) {
          return;
        }
        state.noteStatusMap = {
          ...state.noteStatusMap,
          ...action.payload,
        };
        state.notesStatusRequestId = null;
      })
      .addCase(getAllEvaluations.fulfilled, state => {
        state.noteStatusMap = {};
        state.notesStatusRequestId = null;
      })
      .addCase(deleteEvaluation.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(createEvaluation, updateEvaluation), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEvaluations, getAllEvaluations, getEvaluation), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isFulfilled(getEvaluations, getAllEvaluations), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = action.payload.data?.length || 0;
      })
      .addMatcher(isPending(createEvaluation, updateEvaluation, deleteEvaluation), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isRejected(createEvaluation, updateEvaluation, deleteEvaluation), (state, action) => {
        state.errorMessage = action.error.message || null;
        state.updateSuccess = false;
        state.updating = false;
      });
  },
});

export const { reset } = EvaluationSlice.actions;

export default EvaluationSlice.reducer;
